import React, { useState, useEffect } from 'react';
import { 
  Activity, AlertTriangle, CheckCircle, XCircle, RefreshCw, 
  Server, Database, HardDrive, Cpu, TrendingUp, Shield,
  Package, Clock, GitBranch, AlertCircle, Eye, EyeOff
} from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [workflowStats, setWorkflowStats] = useState(null);
  
  // System Health state variables
  const [healthData, setHealthData] = useState(null);
  const [dockerData, setDockerData] = useState(null);
  const [vulnerabilities, setVulnerabilities] = useState(null);
  const [temporalData, setTemporalData] = useState(null);
  const [versions, setVersions] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [pythonPackagesPage, setPythonPackagesPage] = useState(1);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [dashResponse, workflowResponse] = await Promise.all([
        axios.get(`/api/v1/admin/dashboard`),
        axios.get(`/api/v1/workflow/statistics/`).catch(() => ({ data: null }))
      ]);
      if (dashResponse.data.success) {
        setDashboardData(dashResponse.data.data);
        setLastUpdate(new Date());
        setError(null);
      }
      if (workflowResponse.data?.status === 'success') {
        setWorkflowStats(workflowResponse.data.statistics);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Fetch all endpoints, but don't let individual failures break the whole page
      const [health, docker, vulns, vers, temporal] = await Promise.allSettled([
        axios.get(`/api/v1/admin/health`),
        axios.get(`/api/v1/admin/docker`),
        axios.get(`/api/v1/admin/vulnerabilities`),
        axios.get(`/api/v1/admin/versions`),
        axios.get(`/api/v1/temporal/queue-status`)
      ]);

      // Set data only if the request succeeded
      if (health.status === 'fulfilled') {
        setHealthData(health.value.data.data || health.value.data);
      } else {
        console.error('Health API error:', health.reason);
      }
      
      if (docker.status === 'fulfilled') {
        setDockerData(docker.value.data.data || docker.value.data);
      } else {
        console.error('Docker API error:', docker.reason);
      }
      
      if (vulns.status === 'fulfilled') {
        setVulnerabilities(vulns.value.data.data || vulns.value.data);
      } else {
        console.error('Vulnerabilities API error:', vulns.reason);
      }
      
      if (vers.status === 'fulfilled') {
        setVersions(vers.value.data.data || vers.value.data);
      } else {
        console.error('Versions API error:', vers.reason);
      }
      
      if (temporal.status === 'fulfilled') {
        setTemporalData(temporal.value.data);
      } else {
        console.error('Temporal API error:', temporal.reason);
        // Set default empty data for temporal to avoid breaking the UI
        setTemporalData({ active_workflows: 0, completed_today: 0, pending_tasks: 0, failed_tasks: 0 });
      }
    } catch (error) {
      console.error('Error fetching health data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchAllData();
    
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchDashboardData();
        fetchAllData();
      }, 30000); // Refresh every 30 seconds
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  // Reset pagination when versions data changes
  useEffect(() => {
    setPythonPackagesPage(1);
  }, [versions]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'text-green-600 bg-green-100';
      case 'warning':
        return 'text-yellow-600 bg-yellow-100';
      case 'critical':
      case 'error':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="w-5 h-5" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5" />;
      case 'critical':
      case 'error':
        return <XCircle className="w-5 h-5" />;
      default:
        return <AlertCircle className="w-5 h-5" />;
    }
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      critical: 'bg-red-100 text-red-800',
      warning: 'bg-yellow-100 text-yellow-800',
      info: 'bg-blue-100 text-blue-800'
    };
    return colors[severity] || colors.info;
  };

  const restartContainer = async (containerName) => {
    if (!confirm(`Are you sure you want to restart ${containerName}?`)) return;
    
    try {
      await axios.post(`/api/v1/admin/restart-container`, {
        container_name: containerName,
        admin_user: 'admin'
      });
      alert(`✅ Container ${containerName} restarted successfully`);
      await fetchAllData(); // Refresh data
    } catch (error) {
      alert(`❌ Failed to restart container: ${error.message}`);
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      running: 'bg-green-100 text-green-800',
      exited: 'bg-red-100 text-red-800',
      created: 'bg-yellow-100 text-yellow-800',
      restarting: 'bg-blue-100 text-blue-800',
      paused: 'bg-gray-100 text-gray-800',
      dead: 'bg-red-100 text-red-800',
      healthy: 'bg-green-100 text-green-800',
      unhealthy: 'bg-red-100 text-red-800',
      starting: 'bg-blue-100 text-blue-800',
      unknown: 'bg-gray-100 text-gray-800',
      connected: 'bg-green-100 text-green-800',
      disconnected: 'bg-red-100 text-red-800',
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800'
    };
    return statusClasses[status] || statusClasses.unknown;
  };

  if (loading && !dashboardData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (error && !dashboardData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <XCircle className="w-8 h-8 mx-auto mb-4 text-red-600" />
          <p className="text-red-600 mb-4">Error loading dashboard: {error}</p>
          <button 
            onClick={fetchDashboardData}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }


  const stats = dashboardData?.quick_stats || {};
  // Helper: filter out one-off/expected-to-exit containers
  const isOneOffContainer = (name) => {
    // Regex for common one-off/ephemeral container patterns
    return /(_init|_runner|_migration|_job|_oneoff|_test|_seed|_setup|_backup|_tmp|_cleanup|_cron|_once|_exit|\.1|\.2|\.3|\.bak|\.tmp)/i.test(name);
  };
  // Filter out critical alerts for one-off/expected-to-exit containers
  const filteredAlerts = (dashboardData?.alerts || []).filter(alert => {
    if (alert.severity?.toLowerCase() !== 'critical') return true;
    // Try to extract container name from alert.message
    const match = alert.message.match(/container ([^ ]+)/i);
    if (match && isOneOffContainer(match[1])) return false;
    return true;
  });
  const alerts = filteredAlerts;
  const changes = dashboardData?.recent_changes || [];

  return (
    <div className="w-full">
      <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Admin Dashboard</h1>
          
          {/* Tabs */}
          <div className="flex gap-2 border-b">
            {['overview', 'system-health', 'temporal', 'temporal-queue', 'docker', 'vulnerabilities', 'versions'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 font-medium capitalize ${
                  activeTab === tab
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab === 'temporal-queue' ? 'Temporal Queue' : tab === 'system-health' ? 'System Health' : tab}
              </button>
            ))}
          </div>
        </div>

        {/* Overview Tab - Original Admin Content */}
        {activeTab === 'overview' && (
          <>
            {/* Header Controls */}
            <div className="mb-6 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                Last updated: {lastUpdate?.toLocaleTimeString()}
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-700">Auto-refresh</span>
                </label>
                <button
                  onClick={() => { fetchDashboardData(); fetchAllData(); }}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>
            </div>

            {/* Overall Status Banner */}
            {dashboardData && (
              <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${getStatusColor(dashboardData?.overall_status)}`}>
                {getStatusIcon(dashboardData?.overall_status)}
                <div>
                  <h2 className="font-semibold text-lg">
                    System Status: {dashboardData?.overall_status?.toUpperCase()}
                  </h2>
                  <p className="text-sm opacity-90">
                    {alerts.length > 0 
                      ? `${alerts.length} active alert${alerts.length > 1 ? 's' : ''} requiring attention`
                      : 'All systems operational'}
                  </p>
                </div>
              </div>
            )}

            {/* Workflow Review Statistics */}
            {workflowStats && (
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="text-sm text-gray-600 mb-1">Total Reviews</div>
                  <div className="text-2xl font-bold text-gray-900">{workflowStats.total_reviews || 0}</div>
                </div>
                <div className="bg-green-50 rounded-lg shadow p-4">
                  <div className="text-sm text-green-600 mb-1">Approved</div>
                  <div className="text-2xl font-bold text-green-700">{workflowStats.approved || 0}</div>
                </div>
                <div className="bg-red-50 rounded-lg shadow p-4">
                  <div className="text-sm text-red-600 mb-1">Rejected</div>
                  <div className="text-2xl font-bold text-red-700">{workflowStats.rejected || 0}</div>
                </div>
                <div className="bg-blue-50 rounded-lg shadow p-4">
                  <div className="text-sm text-blue-600 mb-1">In Progress</div>
                  <div className="text-2xl font-bold text-blue-700">{workflowStats.in_progress || 0}</div>
                </div>
                <div className="bg-yellow-50 rounded-lg shadow p-4">
                  <div className="text-sm text-yellow-600 mb-1">Pending</div>
                  <div className="text-2xl font-bold text-yellow-700">{workflowStats.pending || 0}</div>
                </div>
              </div>
            )}

            {/* Quick Stats Grid */}
            {dashboardData && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                {/* System Resources */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-gray-700 font-semibold">CPU Usage</h3>
                    <Cpu className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-2">
                    {stats.system_cpu?.toFixed(1)}%
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${stats.system_cpu > 80 ? 'bg-red-600' : 'bg-blue-600'}`}
                      style={{ width: `${Math.min(stats.system_cpu, 100)}%` }}
                    />
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-gray-700 font-semibold">Memory Usage</h3>
                    <HardDrive className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-2">
                    {stats.system_memory?.toFixed(1)}%
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${stats.system_memory > 85 ? 'bg-red-600' : 'bg-purple-600'}`}
                      style={{ width: `${Math.min(stats.system_memory, 100)}%` }}
                    />
                  </div>
                </div>

                {/* Docker Containers */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-gray-700 font-semibold">Docker Containers</h3>
                    <Server className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-green-600">{stats.docker_running || 0}</span>
                    <span className="text-gray-600">running</span>
                  </div>
                  {stats.docker_stopped > 0 && (
                    <div className="mt-2 text-red-600 text-sm font-semibold">
                      {stats.docker_stopped} stopped
                    </div>
                  )}
                </div>

                {/* Services Status */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-gray-700 font-semibold">Services</h3>
                    <Database className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="flex items-baseline gap-2">
                    {stats.services_down === 0 ? (
                      <>
                        <CheckCircle className="w-8 h-8 text-green-600" />
                        <span className="text-lg text-green-600 font-semibold">All Online</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-8 h-8 text-red-600" />
                        <span className="text-3xl font-bold text-red-600">{stats.services_down}</span>
                        <span className="text-gray-600">down</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Security & Vulnerabilities */}
            {dashboardData && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-gray-700 font-semibold">Critical Alerts</h3>
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="text-4xl font-bold text-red-600">
                    {stats.critical_alerts || 0}
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-gray-700 font-semibold">Warning Alerts</h3>
                    <AlertCircle className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div className="text-4xl font-bold text-yellow-600">
                    {stats.warning_alerts || 0}
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-gray-700 font-semibold">Vulnerabilities</h3>
                    <Shield className="w-5 h-5 text-orange-600" />
                  </div>
                  <div className="flex gap-4">
                    <div>
                      <div className="text-2xl font-bold text-red-600">
                        {stats.vulnerabilities_critical || 0}
                      </div>
                      <div className="text-xs text-gray-600">Critical</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-orange-600">
                        {stats.vulnerabilities_high || 0}
                      </div>
                      <div className="text-xs text-gray-600">High</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Alerts Section */}
            {alerts.length > 0 && (
              <div className="bg-white rounded-lg shadow mb-6">
                <div className="p-6 border-b">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <AlertTriangle className="w-6 h-6 text-orange-600" />
                    Active Alerts
                  </h2>
                </div>
                <div className="divide-y">
                  {alerts.map((alert, idx) => (
                    <div key={idx} className="p-4 hover:bg-gray-50">
                      <div className="flex items-start gap-3">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getSeverityBadge(alert.severity)}`}>
                          {alert.severity?.toUpperCase()}
                        </span>
                        <div className="flex-1">
                          <p className="text-gray-900 font-medium">{alert.message}</p>
                          <p className="text-sm text-gray-500 mt-1">
                            {new Date(alert.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Changes */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Clock className="w-6 h-6 text-blue-600" />
                  Recent Changes
                </h2>
              </div>
              <div className="divide-y max-h-96 overflow-y-auto">
                {changes.length > 0 ? (
                  changes.map((change, idx) => (
                    <div key={idx} className="p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                              {change.change_type}
                            </span>
                            <span className="text-sm text-gray-600">{change.component}</span>
                          </div>
                          <p className="text-gray-900">{change.description}</p>
                          <p className="text-sm text-gray-500 mt-1">
                            By {change.applied_by} • {new Date(change.applied_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    No recent changes recorded
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* System Health Tab */}
        {activeTab === 'system-health' && (
          loading && !healthData ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
                <p className="text-gray-600">Loading system health data...</p>
              </div>
            </div>
          ) : !healthData ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-yellow-600" />
                <div>
                  <h3 className="font-semibold text-yellow-900">No Health Data Available</h3>
                  <p className="text-sm text-yellow-800 mt-1">
                    Unable to fetch system health data. Please check if the backend is running.
                  </p>
                  <button
                    onClick={fetchAllData}
                    className="mt-3 px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 text-sm flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Retry
                  </button>
                </div>
              </div>
            </div>
          ) : (
          <div className="space-y-6">
            {/* System Resources */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Server className="w-6 h-6 text-blue-600" />
                  System Resources
                </h2>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <div className="text-sm text-gray-600 mb-2">CPU Usage</div>
                  <div className="text-3xl font-bold mb-2">
                    {healthData.system?.cpu_percent?.toFixed(1)}%
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${healthData.system?.cpu_percent}%` }}
                    />
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600 mb-2">Memory Usage</div>
                  <div className="text-3xl font-bold mb-2">
                    {healthData.system?.memory_percent?.toFixed(1)}%
                  </div>
                  <div className="text-sm text-gray-500">
                    {healthData.system?.memory_available_gb} GB available
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600 mb-2">Disk Usage</div>
                  <div className="text-3xl font-bold mb-2">
                    {healthData.system?.disk_percent?.toFixed(1)}%
                  </div>
                  <div className="text-sm text-gray-500">
                    {healthData.system?.disk_free_gb} GB free
                  </div>
                </div>
              </div>
            </div>

            {/* Database & Redis */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow">
                <div className="p-6 border-b">
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <Database className="w-6 h-6 text-purple-600" />
                    Database
                  </h2>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-gray-600">Status</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusBadge(healthData.database?.status)}`}>
                      {healthData.database?.status}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Size</span>
                      <span className="font-semibold">{healthData.database?.size_mb} MB</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Connections</span>
                      <span className="font-semibold">{healthData.database?.active_connections}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Host</span>
                      <span className="font-semibold">{healthData.database?.host}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow">
                <div className="p-6 border-b">
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <Database className="w-6 h-6 text-red-600" />
                    Redis
                  </h2>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-gray-600">Status</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusBadge(healthData.redis?.status)}`}>
                      {healthData.redis?.status}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Version</span>
                      <span className="font-semibold">{healthData.redis?.version}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Memory</span>
                      <span className="font-semibold">{healthData.redis?.used_memory_mb} MB</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Clients</span>
                      <span className="font-semibold">{healthData.redis?.connected_clients}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          )
        )}

        {/* Temporal Tab */}
        {activeTab === 'temporal' && (
          <div className="space-y-6">
            {/* Temporal Workflow Status */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <GitBranch className="w-6 h-6 text-purple-600" />
                  Temporal Workflow Queue
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Real-time status of Temporal workflows and task queues
                </p>
              </div>
              <div className="p-6">
                {!temporalData && loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600\" />
                      <p className="text-gray-600\">Loading Temporal data...</p>
                    </div>
                  </div>
                ) : !temporalData ? (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="w-6 h-6 text-yellow-600" />
                      <div>
                        <h3 className="font-semibold text-yellow-900">Temporal Service Unavailable</h3>
                        <p className="text-sm text-yellow-800 mt-1">
                          Unable to connect to Temporal service. This may be normal if Temporal is not configured or running.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                      <div className="text-center p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                        <div className="text-2xl font-bold text-blue-600">{temporalData.active_workflows || 0}</div>
                        <p className="text-sm text-gray-600">Active Workflows</p>
                      </div>
                      <div className="text-center p-4 bg-green-50 rounded-lg border-2 border-green-200">
                        <div className="text-2xl font-bold text-green-600">{temporalData.completed_today || 0}</div>
                        <p className="text-sm text-gray-600">Completed Today</p>
                      </div>
                      <div className="text-center p-4 bg-yellow-50 rounded-lg border-2 border-yellow-200">
                        <div className="text-2xl font-bold text-yellow-600">{temporalData.pending_tasks || 0}</div>
                        <p className="text-sm text-gray-600">Pending Tasks</p>
                      </div>
                      <div className="text-center p-4 bg-red-50 rounded-lg border-2 border-red-200">
                        <div className="text-2xl font-bold text-red-600">{temporalData.failed_tasks || 0}</div>
                        <p className="text-sm text-gray-600">Failed Tasks</p>
                      </div>
                    </div>

                    {/* Task Queues */}
                    {temporalData.queues && temporalData.queues.length > 0 ? (
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold">Task Queues</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {temporalData.queues.map((queue, index) => (
                            <div key={index} className="bg-white border rounded-lg p-4">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="font-semibold text-gray-900">{queue.name || `Queue ${index + 1}`}</h4>
                                <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusBadge(queue.status || 'active')}`}>
                                  {queue.status || 'Active'}
                                </span>
                              </div>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Active Tasks:</span>
                                  <span className="font-semibold text-blue-600">{queue.active_tasks || 0}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Pending Tasks:</span>
                                  <span className="font-semibold text-yellow-600">{queue.pending_tasks || 0}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Avg Processing Time:</span>
                                  <span className="font-semibold">{queue.avg_processing_time || 'N/A'}</span>
                                </div>
                                {queue.last_activity && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Last Activity:</span>
                                    <span className="font-semibold text-xs">{new Date(queue.last_activity).toLocaleString()}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold">Task Queues</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Default Queues when no data */}
                          <div className="bg-white border rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-semibold text-gray-900">Merchant Processing</h4>
                              <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-semibold">
                                Active
                              </span>
                            </div>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Active Tasks:</span>
                                <span className="font-semibold text-blue-600">0</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Pending Tasks:</span>
                                <span className="font-semibold text-yellow-600">0</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Avg Processing Time:</span>
                                <span className="font-semibold">N/A</span>
                              </div>
                            </div>
                          </div>

                          <div className="bg-white border rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-semibold text-gray-900">MCC Classification</h4>
                              <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-semibold">
                                Active
                              </span>
                            </div>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Active Tasks:</span>
                                <span className="font-semibold text-blue-600">0</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Pending Tasks:</span>
                                <span className="font-semibold text-yellow-600">0</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Avg Processing Time:</span>
                                <span className="font-semibold">N/A</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Recent Workflow Activity */}
                    <div className="mt-6">
                      <h3 className="text-lg font-semibold mb-4">Recent Workflow Activity</h3>
                      {temporalData.recent_workflows && temporalData.recent_workflows.length > 0 ? (
                        <div className="space-y-2">
                          {temporalData.recent_workflows.map((workflow, index) => (
                            <div key={index} className="bg-white border rounded-lg p-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="font-semibold">{workflow.workflow_id}</div>
                                  <div className="text-sm text-gray-600">{workflow.workflow_type}</div>
                                </div>
                                <div className="text-right">
                                  <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusBadge(workflow.status)}`}>
                                    {workflow.status}
                                  </span>
                                  <div className="text-xs text-gray-500 mt-1">
                                    {new Date(workflow.start_time).toLocaleString()}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="bg-gray-50 rounded-lg p-4 text-center text-gray-500">
                          <GitBranch className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                          <p>No recent workflow activity</p>
                          <p className="text-sm">Workflow data will appear here when Temporal is active</p>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Temporal Server Status */}
                <div className="mt-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border-2 border-purple-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-purple-900">Temporal Server Status</h3>
                      <p className="text-sm text-purple-700 mt-1">
                        Distributed workflow orchestration system
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 rounded text-sm font-semibold ${
                        temporalData?.server_status === 'connected' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {temporalData?.server_status === 'connected' ? 'Connected' : 'Not Connected'}
                      </span>
                      <button
                        onClick={fetchAllData}
                        className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm"
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Refresh
                      </button>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-purple-700">Server URL:</span>
                      <span className="font-mono">{temporalData?.server_url || 'localhost:7233'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-purple-700">Namespace:</span>
                      <span className="font-mono">{temporalData?.namespace || 'default'}</span>
                    </div>
                    {temporalData?.last_updated && (
                      <div className="flex justify-between col-span-2">
                        <span className="text-purple-700">Last Updated:</span>
                        <span className="font-mono text-xs">{new Date(temporalData.last_updated).toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Temporal Queue Tab */}
        {activeTab === 'temporal-queue' && (
          <div className="space-y-6">
            {/* Real-time Queue Status */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <GitBranch className="w-6 h-6 text-purple-600" />
                  Temporal Queue Monitor
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Real-time workflow monitoring with batch status by time periods
                </p>
              </div>
              <div className="p-6">
                {temporalData ? (
                  <>
                    {/* Real-time Metrics */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                      <div className="text-center p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                        <div className="text-2xl font-bold text-blue-600">{temporalData.active_workflows || 0}</div>
                        <p className="text-sm text-gray-600">Active Workflows</p>
                        <p className="text-xs text-blue-500 mt-1">Real-time</p>
                      </div>
                      <div className="text-center p-4 bg-green-50 rounded-lg border-2 border-green-200">
                        <div className="text-2xl font-bold text-green-600">{temporalData.completed_today || 0}</div>
                        <p className="text-sm text-gray-600">Completed Today</p>
                        <p className="text-xs text-green-500 mt-1">Last 24h</p>
                      </div>
                      <div className="text-center p-4 bg-yellow-50 rounded-lg border-2 border-yellow-200">
                        <div className="text-2xl font-bold text-yellow-600">{temporalData.pending_tasks || 0}</div>
                        <p className="text-sm text-gray-600">Pending Tasks</p>
                        <p className="text-xs text-yellow-500 mt-1">Queued</p>
                      </div>
                      <div className="text-center p-4 bg-red-50 rounded-lg border-2 border-red-200">
                        <div className="text-2xl font-bold text-red-600">{temporalData.failed_tasks || 0}</div>
                        <p className="text-sm text-gray-600">Failed Tasks</p>
                        <p className="text-xs text-red-500 mt-1">Errors</p>
                      </div>
                    </div>

                    {/* Upload Batches by Time Period */}
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <Package className="w-5 h-5 text-purple-600" />
                        Upload Batches by Time Period
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                          { period: 'Daily', count: temporalData.daily_batches || 0, color: 'blue' },
                          { period: 'Weekly', count: temporalData.weekly_batches || 0, color: 'green' },
                          { period: 'Monthly', count: temporalData.monthly_batches || 0, color: 'purple' },
                          { period: 'Quarterly', count: temporalData.quarterly_batches || 0, color: 'orange' }
                        ].map((batch) => (
                          <div key={batch.period} className={`text-center p-4 bg-${batch.color}-50 rounded-lg border-2 border-${batch.color}-200`}>
                            <div className={`text-2xl font-bold text-${batch.color}-600`}>{batch.count}</div>
                            <p className="text-sm text-gray-600">{batch.period}</p>
                            <p className="text-xs text-gray-500 mt-1">Batches</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Enhanced Task Queues with Real-time Status */}
                    {temporalData.queues && temporalData.queues.length > 0 ? (
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold flex items-center gap-2">
                          <Server className="w-5 h-5 text-indigo-600" />
                          Active Task Queues
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {temporalData.queues.map((queue, index) => (
                            <div key={index} className="bg-gradient-to-br from-white to-gray-50 border rounded-lg p-4 shadow-sm">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                                  <div className={`w-2 h-2 rounded-full ${queue.status === 'active' ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                                  {queue.name || `Queue ${index + 1}`}
                                </h4>
                                <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusBadge(queue.status || 'active')}`}>
                                  {queue.status || 'Active'}
                                </span>
                              </div>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Active Tasks:</span>
                                  <span className="font-semibold text-blue-600">{queue.active_tasks || 0}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Pending Tasks:</span>
                                  <span className="font-semibold text-yellow-600">{queue.pending_tasks || 0}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Completed Tasks:</span>
                                  <span className="font-semibold text-green-600">{queue.completed_tasks || 0}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Avg Processing Time:</span>
                                  <span className="font-semibold">{queue.avg_processing_time || 'N/A'}</span>
                                </div>
                                {queue.last_activity && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Last Activity:</span>
                                    <span className="font-semibold text-xs">{new Date(queue.last_activity).toLocaleString()}</span>
                                  </div>
                                )}
                              </div>
                              {/* Progress Bar */}
                              {queue.active_tasks > 0 && (
                                <div className="mt-3">
                                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                                    <span>Queue Progress</span>
                                    <span>{Math.round((queue.completed_tasks / (queue.completed_tasks + queue.pending_tasks + queue.active_tasks)) * 100)}%</span>
                                  </div>
                                  <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div 
                                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                      style={{ 
                                        width: `${Math.round((queue.completed_tasks / (queue.completed_tasks + queue.pending_tasks + queue.active_tasks)) * 100)}%` 
                                      }}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold flex items-center gap-2">
                          <Server className="w-5 h-5 text-indigo-600" />
                          Active Task Queues
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Default Queues when no data */}
                          <div className="bg-gradient-to-br from-white to-gray-50 border rounded-lg p-4 shadow-sm">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                Merchant Processing
                              </h4>
                              <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-semibold">
                                Active
                              </span>
                            </div>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Active Tasks:</span>
                                <span className="font-semibold text-blue-600">0</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Pending Tasks:</span>
                                <span className="font-semibold text-yellow-600">0</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Completed Tasks:</span>
                                <span className="font-semibold text-green-600">0</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Avg Processing Time:</span>
                                <span className="font-semibold">N/A</span>
                              </div>
                            </div>
                          </div>

                          <div className="bg-gradient-to-br from-white to-gray-50 border rounded-lg p-4 shadow-sm">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                MCC Classification
                              </h4>
                              <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-semibold">
                                Active
                              </span>
                            </div>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Active Tasks:</span>
                                <span className="font-semibold text-blue-600">0</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Pending Tasks:</span>
                                <span className="font-semibold text-yellow-600">0</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Completed Tasks:</span>
                                <span className="font-semibold text-green-600">0</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Avg Processing Time:</span>
                                <span className="font-semibold">N/A</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Real-time Workflow Activity Feed */}
                    <div className="mt-6">
                      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <RefreshCw className="w-5 h-5 text-green-600" />
                        Real-time Workflow Activity
                      </h3>
                      {temporalData.recent_workflows && temporalData.recent_workflows.length > 0 ? (
                        <div className="space-y-2 max-h-80 overflow-y-auto">
                          {temporalData.recent_workflows.map((workflow, index) => (
                            <div key={index} className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className={`w-3 h-3 rounded-full ${
                                    workflow.status === 'completed' ? 'bg-green-500' :
                                    workflow.status === 'running' ? 'bg-blue-500' :
                                    workflow.status === 'failed' ? 'bg-red-500' : 'bg-yellow-500'
                                  }`}></div>
                                  <div>
                                    <div className="font-semibold text-sm">{workflow.workflow_id}</div>
                                    <div className="text-xs text-gray-600">{workflow.workflow_type}</div>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusBadge(workflow.status)}`}>
                                    {workflow.status}
                                  </span>
                                  <div className="text-xs text-gray-500 mt-1">
                                    {new Date(workflow.start_time).toLocaleString()}
                                  </div>
                                </div>
                              </div>
                              {workflow.duration && (
                                <div className="mt-2 text-xs text-gray-500">
                                  Duration: {workflow.duration}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="bg-gray-50 rounded-lg p-6 text-center text-gray-500">
                          <RefreshCw className="w-12 h-12 mx-auto mb-2 text-gray-400 animate-spin" />
                          <p>No recent workflow activity</p>
                          <p className="text-sm">Real-time updates will appear here when Temporal workflows are active</p>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <RefreshCw className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
                    <p className="text-gray-600">Loading Temporal queue status...</p>
                    <p className="text-sm text-gray-500 mt-2">Connecting to Temporal server for real-time monitoring</p>
                  </div>
                )}

                {/* Enhanced Temporal Server Status */}
                <div className="mt-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6 border-2 border-purple-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-purple-900">Temporal Server Connection</h3>
                      <p className="text-sm text-purple-700 mt-1">
                        Real-time workflow orchestration and queue monitoring
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded text-sm font-semibold ${
                        temporalData?.server_status === 'connected' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {temporalData?.server_status === 'connected' ? '🟢 Connected' : '🟡 Connecting...'}
                      </span>
                      <button
                        onClick={fetchAllData}
                        className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm flex items-center gap-2"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Refresh
                      </button>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-purple-700">Server:</span>
                      <span className="font-mono text-xs">{temporalData?.server_url || 'localhost:7233'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-purple-700">Namespace:</span>
                      <span className="font-mono text-xs">{temporalData?.namespace || 'default'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-purple-700">Queues:</span>
                      <span className="font-semibold text-purple-900">{temporalData?.queues?.length || 0}</span>
                    </div>
                    {temporalData?.last_updated && (
                      <div className="flex justify-between">
                        <span className="text-purple-700">Updated:</span>
                        <span className="font-mono text-xs">{new Date(temporalData.last_updated).toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Docker Tab */}
        {activeTab === 'docker' && dockerData && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Server className="w-6 h-6 text-blue-600" />
                  Docker Containers
                </h2>
                <button
                  onClick={fetchAllData}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="divide-y">
              {dockerData.containers?.map((container) => (
                <div key={container.id} className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold">{container.name}</h3>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusBadge(container.status)}`}>
                          {container.status}
                        </span>
                        {container.health !== 'unknown' && (
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusBadge(container.health)}`}>
                            {container.health}
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Image: </span>
                          <span className="font-medium">{container.image}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">ID: </span>
                          <span className="font-mono text-xs">{container.id}</span>
                        </div>
                      </div>
                    </div>
                    {container.status === 'running' && (
                      <button
                        onClick={() => restartContainer(container.name)}
                        className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 text-sm"
                      >
                        Restart
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Vulnerabilities Tab */}
        {activeTab === 'vulnerabilities' && vulnerabilities && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-sm text-gray-600 mb-2">Critical</div>
                <div className="text-3xl font-bold text-red-600">
                  {vulnerabilities.total_critical}
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-sm text-gray-600 mb-2">High</div>
                <div className="text-3xl font-bold text-orange-600">
                  {vulnerabilities.total_high}
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-sm text-gray-600 mb-2">Medium</div>
                <div className="text-3xl font-bold text-yellow-600">
                  {vulnerabilities.total_medium}
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-sm text-gray-600 mb-2">Low</div>
                <div className="text-3xl font-bold text-blue-600">
                  {vulnerabilities.total_low}
                </div>
              </div>
            </div>

            {/* Tool Status Overview */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Shield className="w-6 h-6 text-orange-600" />
                  Tool Status & Security Overview
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Status of installed tools and security components
                </p>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    { name: 'GO Parser', status: 'installed', version: '1.21+', description: 'Backend parsing engine' },
                    { name: 'Playwright', status: 'installed', version: '1.40+', description: 'Browser automation for screenshots' },
                    { name: 'LLM (Ollama)', status: 'running', version: 'Multiple models', description: 'AI classification engine' },
                    { name: 'MCC Stage 2 LLM', status: 'installed', version: 'OpenRouter/Gemini/OpenAI', description: 'Full-library MCC classification' },
                    { name: 'Temporal', status: 'running', version: '1.22+', description: 'Workflow orchestration' },
                    { name: 'PostgreSQL', status: 'running', version: '15+', description: 'Primary database' },
                    { name: 'Redis', status: 'running', version: '7+', description: 'Cache & session store' },
                    { name: 'Docker', status: 'running', version: '24+', description: 'Container orchestration' }
                  ].map((tool, index) => (
                    <div key={index} className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 border">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-gray-900">{tool.name}</h4>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          tool.status === 'running' ? 'bg-green-100 text-green-800' :
                          tool.status === 'installed' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {tool.status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mb-2">{tool.description}</p>
                      <div className="text-xs text-gray-500">
                        Version: {tool.version}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Python Vulnerabilities */}
            {vulnerabilities.python_packages?.vulnerabilities?.length > 0 && (
              <div className="bg-white rounded-lg shadow">
                <div className="p-6 border-b">
                  <h2 className="text-xl font-bold">Python Package Vulnerabilities</h2>
                </div>
                <div className="divide-y max-h-96 overflow-y-auto">
                  {vulnerabilities.python_packages.vulnerabilities.map((vuln, idx) => (
                    <div key={idx} className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="font-semibold text-gray-900">{vuln.package}</div>
                          <div className="text-sm text-gray-600 mt-1">{vuln.id}</div>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusBadge(vuln.severity?.toLowerCase())}`}>
                          {vuln.severity}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {vulnerabilities.python_packages?.status === 'tool_not_installed' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-yellow-800">
                  {vulnerabilities.python_packages.message}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Versions Tab */}
        {activeTab === 'versions' && versions && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Package className="w-6 h-6 text-indigo-600" />
                  Component Versions & Tool Status
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Comprehensive version information for all system components organized by category
                </p>
              </div>
              <div className="divide-y">
                {Object.entries(versions).map(([category, components]) => {
                  // Filter out components with errors or not installed to show only valid data
                  const validComponents = Object.entries(components).filter(([component, info]) => {
                    // For python_packages, check if it has valid packages
                    if (component === 'python_packages' && info.packages) {
                      return Object.values(info.packages).some(pkg => 
                        pkg.status === 'installed' && !pkg.error && pkg.current !== 'unknown'
                      );
                    }
                    // For other components, show if they have valid status and no errors
                    return (info.status === 'running' || info.status === 'installed' || info.status === 'expected' || info.compatible === true) && !info.error;
                  });

                  // Skip categories with no valid components
                  if (validComponents.length === 0) return null;

                  return (
                    <div key={category} className="p-6">
                      {/* Category Header */}
                      <div className="flex items-center gap-3 mb-4">
                        {category === 'Core System Components' && <Server className="w-6 h-6 text-blue-600" />}
                        {category === 'AI & ML Tools' && <Shield className="w-6 h-6 text-purple-600" />}
                        {category === 'Backend Processing Tools' && <Package className="w-6 h-6 text-green-600" />}
                        {category === 'Infrastructure Tools' && <Database className="w-6 h-6 text-orange-600" />}
                        {category === 'Frontend Tools' && <Activity className="w-6 h-6 text-cyan-600" />}
                        {category === 'Backend Frameworks' && <TrendingUp className="w-6 h-6 text-emerald-600" />}
                        {category === 'Backend Dependencies' && <Package className="w-6 h-6 text-indigo-600" />}
                        {category === 'Microservices' && <Server className="w-6 h-6 text-teal-600" />}
                        {!['Core System Components', 'AI & ML Tools', 'Backend Processing Tools', 'Infrastructure Tools', 'Frontend Tools', 'Backend Frameworks', 'Backend Dependencies', 'Microservices'].includes(category) && <Package className="w-6 h-6 text-gray-600" />}
                        <h3 className="text-lg font-bold text-gray-900">{category}</h3>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {validComponents.map(([component, info]) => {
                        // Handle special case for python_packages with nested packages
                        if (component === 'python_packages' && info.packages) {
                          const packages = Object.values(info.packages);
                          const pageSize = 12;
                          const totalPages = Math.ceil(packages.length / pageSize);
                          const currentPage = pythonPackagesPage;
                          
                          const startIndex = (currentPage - 1) * pageSize;
                          const endIndex = startIndex + pageSize;
                          const currentPackages = packages.slice(startIndex, endIndex);
                          
                          return (
                            <div key={component} className="md:col-span-2 lg:col-span-3">
                              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg p-4 border border-indigo-200 mb-4">
                                <div className="flex items-center justify-between mb-3">
                                  <h4 className="font-semibold text-gray-900">{info.name || component}</h4>
                                  <div className="flex items-center gap-2">
                                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                      info.installed_count === info.total_packages ? 'bg-green-100 text-green-800' :
                                      'bg-yellow-100 text-yellow-800'
                                    }`}>
                                      {info.installed_count}/{info.total_packages} Installed
                                    </span>
                                    {totalPages > 1 && (
                                      <div className="flex items-center gap-1">
                                        <button
                                          onClick={() => setPythonPackagesPage(Math.max(1, currentPage - 1))}
                                          disabled={currentPage === 1}
                                          className="px-2 py-1 text-xs bg-gray-200 rounded disabled:opacity-50"
                                        >
                                          ‹
                                        </button>
                                        <span className="text-xs text-gray-600">
                                          {currentPage}/{totalPages}
                                        </span>
                                        <button
                                          onClick={() => setPythonPackagesPage(Math.min(totalPages, currentPage + 1))}
                                          disabled={currentPage === totalPages}
                                          className="px-2 py-1 text-xs bg-gray-200 rounded disabled:opacity-50"
                                        >
                                          ›
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                {info.description && (
                                  <p className="text-xs text-gray-600 mb-3">{info.description}</p>
                                )}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                  {currentPackages
                                    .map((pkgInfo, idx) => ({ pkgInfo, pkgName: Object.keys(info.packages)[startIndex + idx] }))
                                    .filter(({ pkgInfo }) => pkgInfo.status === 'installed' && !pkgInfo.error && pkgInfo.current !== 'unknown')
                                    .map(({ pkgInfo, pkgName }) => (
                                      <div key={pkgName} className="bg-white rounded border p-3">
                                        <div className="flex items-center justify-between mb-2">
                                          <h5 className="font-medium text-gray-900 text-sm">{pkgName}</h5>
                                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                            pkgInfo.compatible === true ? 'bg-green-100 text-green-800' :
                                            pkgInfo.compatible === false ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-blue-100 text-blue-800'
                                          }`}>
                                            {pkgInfo.compatible === true ? '✓ Compatible' :
                                             pkgInfo.compatible === false ? 'Incompatible' : 'Installed'}
                                          </span>
                                        </div>
                                        {pkgInfo.current && pkgInfo.current !== 'unknown' && (
                                          <p className="text-xs text-gray-700 mb-1">
                                            Current: <span className="font-mono">{pkgInfo.current}</span>
                                          </p>
                                        )}
                                        {pkgInfo.required && (
                                          <p className="text-xs text-gray-700 mb-1">
                                            Required: <span className="font-mono">{pkgInfo.required}</span>
                                          </p>
                                        )}
                                        {pkgInfo.description && (
                                          <p className="text-xs text-gray-600">{pkgInfo.description}</p>
                                        )}
                                      </div>
                                    ))
                                  }
                                </div>
                              </div>
                            </div>
                          );
                        }
                        
                        // Regular component rendering
                        return (
                          <div key={component} className={`bg-gradient-to-r rounded-lg p-4 border ${
                            category === 'Core System Components' ? 'from-blue-50 to-indigo-50 border-blue-200' :
                            category === 'AI & ML Tools' ? 'from-purple-50 to-pink-50 border-purple-200' :
                            category === 'Backend Processing Tools' ? 'from-green-50 to-teal-50 border-green-200' :
                            category === 'Infrastructure Tools' ? 'from-orange-50 to-yellow-50 border-orange-200' :
                            category === 'Frontend Tools' ? 'from-cyan-50 to-blue-50 border-cyan-200' :
                            category === 'Backend Frameworks' ? 'from-emerald-50 to-teal-50 border-emerald-200' :
                            category === 'Backend Dependencies' ? 'from-indigo-50 to-blue-50 border-indigo-200' :
                            category === 'Microservices' ? 'from-teal-50 to-cyan-50 border-teal-200' :
                            'from-gray-50 to-gray-100 border-gray-200'
                          }`}>
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-semibold text-gray-900">{info.name || component}</h4>
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                info.status === 'running' || info.status === 'installed' || info.status === 'expected' || info.compatible === true ? 'bg-green-100 text-green-800' :
                                info.status === 'error' || info.status === 'not_installed' || info.compatible === false ? 'bg-red-100 text-red-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {info.status === 'running' ? 'Running' :
                                 info.status === 'installed' ? 'Installed' :
                                 info.status === 'expected' ? 'Expected' :
                                 info.status === 'error' ? 'Error' :
                                 info.status === 'not_installed' ? 'Not Installed' :
                                 info.compatible === true ? 'Compatible' :
                                 info.compatible === false ? 'Incompatible' : 'Unknown'}
                              </span>
                            </div>
                            {info.current && (
                              <p className="text-sm text-gray-700 mb-1">
                                Current: <span className="font-mono">{info.current}</span>
                              </p>
                            )}
                            {info.required && (
                              <p className="text-sm text-gray-700 mb-1">
                                Required: <span className="font-mono">{info.required}</span>
                              </p>
                            )}
                            {info.api_version && (
                              <p className="text-sm text-gray-700 mb-1">
                                API: <span className="font-mono">{info.api_version}</span>
                              </p>
                            )}
                            {info.description && (
                              <p className="text-xs text-gray-600">{info.description}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
                })}
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default AdminDashboard;
